import { createFileRoute } from '@tanstack/react-router';
import { useTranslate } from '@tolgee/react';
import { AppRoute } from '@agentselly/immodossier-shared';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { useAppParams } from '~/helper/useAppParams';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { RenderWidgets } from '~/components/widget/RenderWidgets';
import {
  EmptyBlock,
  fmtDate,
  Ico,
  NewBadge,
  PageHead,
  V3Page,
} from '~/components/cockpit/v3';
import {
  DOCUMENT_TYPE_LABELS,
  type WidgetProps,
} from '~/components/cockpit/cockpitData';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_DOCUMENTS,
)({
  component: DocumentsPage,
});

interface Doc {
  readonly id?: string;
  readonly type: string;
  readonly title?: string;
  readonly filename?: string;
  readonly url?: string;
  readonly uploadedAt?: string;
  readonly isPrimary?: boolean;
}

const GROUP_ORDER = [
  'seller_contract',
  'land_register',
  'floor_plan',
  'energy_certificate',
  'weitere_dokumente',
  'expose',
  'flyer',
  'photo',
];

function DocumentsPage(): React.ReactElement {
  const params = useAppParams(Route);
  const { t } = useTranslate();

  const query = useLoaderQuery(
    AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_DOCUMENTS,
    { params },
  );

  if (query.isLoading) return <LoadingWidget />;
  if (query.isError || !query.data) return <ErrorWidget error={query.error} />;

  if (!query.data.v3Redesign) {
    const widgetProps = (query.data?.widgetProps || {}) as Record<
      string,
      Record<string, unknown>
    >;
    const widgets = (
      query.data?.widgets || [
        {
          name: 'cockpitDocumentsWidget',
          type: 'cockpitDocumentsWidget',
          cols: 12,
        },
      ]
    ).map((widget: { name: string; type: string; cols: number }) => ({
      ...widget,
      props: widgetProps[widget.name] || {},
    }));

    return (
      <div className="container mx-auto py-6">
        <RenderWidgets widgets={widgets} />
      </div>
    );
  }

  const w =
    ((query.data.widgetProps || {}) as WidgetProps).cockpitDocumentsWidget ??
    {};
  const docs: Doc[] = w.documents ?? [];
  const required: string[] = w.requiredDocumentTypes ?? [];
  const missing: string[] = w.missingDocumentTypes ?? [];
  const label = (type: string, fallback?: string): string =>
    DOCUMENT_TYPE_LABELS[type] ?? fallback ?? type;
  const order = [
    ...new Set([
      ...GROUP_ORDER,
      ...docs.map((d) => d.type),
    ]),
  ].filter((ty) => docs.some((d) => d.type === ty));

  const head = (
    <PageHead
      odId="page-head"
      title={t('cockpit_docs_title', 'Dokumente')}
      sub={t('cockpit_docs_sub', 'Unterlagen zum Objekt')}
    />
  );

  const checklist = required.length ? (
    <section
      className="card"
      data-od-id="missing-documents"
      aria-labelledby="h-ms"
    >
      <div className="sec">
        <h2 id="h-ms">
          {t('cockpit_docs_missing', 'Fehlende Dokumente')}
          <NewBadge />
        </h2>
        <span className="xs mu">
          {required.length - missing.length} {t('cockpit_docs_of', 'von')}{' '}
          {required.length} {t('cockpit_docs_have', 'vorhanden')}
        </span>
      </div>
      <div style={{ marginTop: 8 }}>
        {required.map((r) => {
          const ok = !missing.includes(r);
          return (
            <div
              className={`ck ${ok ? 'ok' : 'miss'}`}
              key={r}
            >
              <span
                className="b"
                aria-hidden="true"
              >
                {ok ? (
                  <Ico
                    name="check"
                    size={14}
                  />
                ) : null}
              </span>
              <span>
                {label(r)}
                <span className="sr">
                  {ok
                    ? ` – ${t('cockpit_docs_present', 'vorhanden')}`
                    : ` – ${t('cockpit_docs_absent', 'fehlt')}`}
                </span>
              </span>
              {ok ? null : (
                <span
                  className="chip c-warn"
                  style={{ marginLeft: 'auto' }}
                >
                  <i />
                  {t('cockpit_docs_absent', 'fehlt')}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  ) : null;

  if (docs.length === 0) {
    return (
      <V3Page>
        {head}
        <EmptyBlock
          icon="folder"
          title={t('cockpit_docs_empty_title', 'Noch keine Dokumente')}
          text={t(
            'cockpit_docs_empty_text',
            'Sobald Ihr Betreuer Verkaufsvertrag, Grundriss, Energieausweis oder das Exposé hochlädt, können Sie alles hier herunterladen.',
          )}
        />
        {checklist}
      </V3Page>
    );
  }

  return (
    <V3Page>
      {head}
      <div
        className="grid split g2"
        style={{ alignItems: 'start' }}
      >
        <section
          className="card"
          data-od-id="document-list"
          data-widget="cockpitDocumentsWidget"
          aria-labelledby="h-dl"
        >
          <div
            className="sec"
            style={{ marginBottom: 12 }}
          >
            <h2 id="h-dl">{t('cockpit_docs_list', 'Unterlagen')}</h2>
            <span className="xs mu">
              {docs.length} {t('cockpit_docs_files', 'Dateien')}
            </span>
          </div>
          {order.map((ty) => {
            const ds = docs.filter((d) => d.type === ty);
            return (
              <div
                className="dg"
                key={ty}
                data-od-id={`group-${ty.replace(/[^a-z_]/g, '')}`}
              >
                <h3>
                  <span>{label(ty, ds[0]?.title)}</span>
                  <span className="num">{ds.length}</span>
                </h3>
                {ds.map((d, i) => {
                  const key = d.id ?? `${ty}-${i}`;
                  const title = d.title || label(d.type);
                  return (
                    <div
                      className="doc"
                      key={key}
                      data-od-id={`doc-${key}`}
                    >
                      <span className="ico">
                        <Ico
                          name={d.type === 'photo' ? 'house' : 'doc'}
                          size={18}
                        />
                      </span>
                      <span className="bd">
                        <span className="t">{title}</span>
                        <span className="fn">
                          {d.filename}
                          {d.uploadedAt ? ` · ${fmtDate(d.uploadedAt)}` : ''}
                        </span>
                      </span>
                      <span className="end">
                        <span className="chip c-ok">
                          <i />
                          {t('cockpit_docs_uploaded', 'hochgeladen')}
                        </span>
                        {d.url ? (
                          <a
                            className="btn"
                            href={d.url}
                            target="_blank"
                            rel="noreferrer"
                            data-od-id={`doc-${key}-download`}
                            aria-label={`${title} ${t(
                              'cockpit_docs_download_aria',
                              'herunterladen',
                            )}`}
                          >
                            <Ico
                              name="arrow"
                              size={16}
                            />
                            {t('cockpit_docs_download', 'Download')}
                          </a>
                        ) : null}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </section>
        {checklist}
      </div>
    </V3Page>
  );
}
